import { useQuery } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { Select } from '../../components/ui/FormField.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useConfirmedMutation } from '../../hooks/useConfirmedMutation.js';
import { useListControls } from '../../hooks/useListControls.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface Brand {
  _id: string;
  name: string;
}
/** One screenful. Matches the other paginated admin lists. */
const PAGE_SIZE = 20;

/** What `/car-models` answers once a page has been asked for (162). */
interface PagedModels {
  items: CarModelRow[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface CarModelRow {
  _id: string;
  name: string;
  brand_id: string;
  status: string;
}

export function CarModelListPage() {
  const ability = useAbility();
  const canManage = ability.can('manage', 'CarModel');
  const [brandId, setBrandId] = useState('');

  const { data: brands } = useQuery({
    queryKey: RESOURCE_KEYS.carBrand.list,
    queryFn: () => api.get<Brand[]>('/car-brands'),
  });
  const brandName = new Map((brands ?? []).map((b) => [b._id, b.name]));

  /**
   * 164: this screen was MISSED by 162. Car brands, categories and features are all thin wrappers
   * over `TaxonomyListPage` and got their pager and search box from that one change — but car
   * models is its own page, because it carries a brand filter. 162's spec said "one change, four
   * screens" and that was true of the FORM pages (`TaxonomyFormPage`) and false of the list pages.
   * Found on staging with 25 models rendering as one unpaged list. `resetOn` carries the brand
   * filter, so changing it returns to page 1 like any other filter (162 FR-007).
   */
  const { page, setPage, searchInput, setSearchInput, search } = useListControls({
    resetOn: [brandId],
  });

  const listPath = (() => {
    const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
    if (brandId) params.set('brand_id', brandId);
    // Only when there is something to filter by: a cleared box must show the whole list (FR-004).
    if (search) params.set('q', search);
    return `/car-models?${params.toString()}`;
  })();

  const { data, isLoading, error } = useQuery({
    queryKey: [...RESOURCE_KEYS.carModel.list, brandId, page, search],
    // 🔴 Asking for a page changes the shape: this endpoint answers a BARE ARRAY to a caller that
    // asks for none, because the customer app reads it that way. See `shared/listResponse.ts`.
    queryFn: () => api.get<PagedModels>(listPath),
    // 063 (FR-010): a refusal is not a transient fault; retrying it only keeps
    // the caller reading the empty state.
    retry: false,
  });

  const remove = useConfirmedMutation({
    mutationFn: (id: string) => api.delete(`/car-models/${id}`),
    invalidateKey: RESOURCE_KEYS.carModel.list,
  });

  const columns: Column<CarModelRow>[] = [
    { key: 'name', header: 'Model' },
    { key: 'brand_id', header: 'Brand', render: (r) => brandName.get(r.brand_id) ?? '—' },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    {
      key: '_id',
      header: 'Actions',
      render: (r) => (
        <RowActions
          actions={[
            canManage && {
              key: 'edit',
              label: 'Edit',
              href: `/car-models/${r._id}/edit`,
              icon: <Pencil className="h-4 w-4" />,
            },
            canManage && {
              key: 'delete',
              label: 'Delete',
              variant: 'danger',
              confirmMessage: `Delete model "${r.name}"?`,
              onClick: () => remove.run(r._id),
              icon: <Trash2 className="h-4 w-4" />,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Car Models"
        description="Vehicle models within a brand (Vios under Toyota…)."
        actions={
          canManage ? (
            <Link to="/car-models/new">
              <Button>+ New</Button>
            </Link>
          ) : undefined
        }
      />
      <Card className="mb-4 max-w-xs">
        <Select
          id="brand_filter"
          aria-label="Brand"
          data-testid="model-brand-filter"
          value={brandId}
          onChange={(e) => setBrandId((e.target as HTMLSelectElement).value)}
        >
          <option value="">All brands</option>
          {brands?.map((b) => (
            <option key={b._id} value={b._id}>
              {b.name}
            </option>
          ))}
        </Select>
      </Card>
      <ListSearch
        value={searchInput}
        onChange={setSearchInput}
        label="Search car models by name"
        testId="car-model-search"
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        keyExtractor={(r) => r._id}
        emptyMessage="No models yet"
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: data?.pages ?? 1,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
